import { useEffect, useState } from "react";
import {
  Check,
  MagnifyingGlass,
  Package,
  Spinner,
  User,
  X,
} from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useLocation } from "react-router-dom";

import { getUsers } from "@/api";
import {
  userBundleAccessQueryKeys,
  useUserBundleAccess,
  useUserCourseAccess,
} from "@/hooks";
import { useBundles } from "@/hooks/useBundles";
import { useCourses } from "@/hooks/useCourses";
import { useDebounce } from "@/hooks/useDebounce";
import i18n from "@/i18n";
import type { ModalProps } from "@/shared.types";
import type { Bundle, Course, User as UserType } from "@/types";
import {
  Button,
  Circle,
  errorToast,
  Heading,
  Modal,
  useToastStore,
} from "@/ui";
import { mapCourseTypeLabel } from "@/utils/courseUtils";

type AccessType = "bundle" | "course";

interface LocationState {
  user?: UserType;
}

export const GrantBundleAccessModal = ({ show, onClose }: ModalProps) => {
  const pushToast = useToastStore.getState().pushToast;
  const queryClient = useQueryClient();
  const location = useLocation();
  const locationState = (location.state ?? {}) as LocationState;
  const preselectedUser = locationState.user ?? null;

  const [accessType, setAccessType] = useState<AccessType>("bundle");
  const [userSearch, setUserSearch] = useState(
    preselectedUser
      ? `${preselectedUser.first_name} ${preselectedUser.last_name} (${preselectedUser.email})`
      : "",
  );
  const [selectedUser, setSelectedUser] = useState<UserType | null>(
    preselectedUser,
  );
  const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [indefinite, setIndefinite] = useState(true);
  const [userResults, setUserResults] = useState<UserType[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const debouncedSearch = useDebounce(userSearch, 300);

  const { useAllBundles } = useBundles();
  const { data: bundlesData } = useAllBundles();
  const bundles = bundlesData?.data ?? [];

  const { useAllCourses } = useCourses();
  const { data: coursesData } = useAllCourses();
  const courses = coursesData?.data ?? [];

  const { useAdminGrantBundleAccess } = useUserBundleAccess();
  const { mutate: grantBundleAccess, isPending: isBundlePending } =
    useAdminGrantBundleAccess();

  const { useAdminGrantCourseAccess } = useUserCourseAccess();
  const { mutate: grantCourseAccess, isPending: isCoursePending } =
    useAdminGrantCourseAccess();

  const isPending = isBundlePending || isCoursePending;

  useEffect(() => {
    if (!debouncedSearch || selectedUser) {
      setUserResults([]);
      return;
    }
    setIsSearching(true);
    getUsers(1, debouncedSearch)
      .then((res) => {
        setUserResults(res.data.slice(0, 6));
        setShowResults(true);
      })
      .catch(() => setUserResults([]))
      .finally(() => setIsSearching(false));
  }, [debouncedSearch, selectedUser]);

  const handleUserSelect = (user: UserType) => {
    setSelectedUser(user);
    setUserSearch(`${user.first_name} ${user.last_name} (${user.email})`);
    setShowResults(false);
  };

  const handleClearUser = () => {
    setSelectedUser(null);
    setUserSearch("");
    setUserResults([]);
    setShowResults(false);
  };

  const handleAccessTypeChange = (type: AccessType) => {
    setAccessType(type);
    setSelectedBundle(null);
    setSelectedCourse(null);
  };

  const canSubmit =
    !!selectedUser &&
    (accessType === "bundle" ? !!selectedBundle : !!selectedCourse);

  const handleGrant = () => {
    if (!canSubmit) return;

    const accessStartDate = startDate || null;
    const accessEndDate = indefinite ? null : endDate || null;

    const onSuccess = () => {
      void queryClient.invalidateQueries({
        queryKey: userBundleAccessQueryKeys.useUserBundleAccess(),
        exact: false,
      });
      void pushToast({
        type: "success",
        title: i18n.t("materials.access_successfully_granted"),
        icon: <Check size={18} />,
      });
      onClose();
    };

    const onError = (err: unknown) => {
      errorToast(err);
    };

    if (accessType === "bundle" && selectedBundle) {
      grantBundleAccess(
        {
          userId: selectedUser.id,
          bundleId: selectedBundle.id,
          accessStartDate,
          accessEndDate,
        },
        { onSuccess, onError },
      );
    } else if (accessType === "course" && selectedCourse) {
      grantCourseAccess(
        {
          userId: selectedUser.id,
          courseId: selectedCourse.id,
          accessStartDate,
          accessEndDate,
        },
        { onSuccess, onError },
      );
    }
  };

  return (
    <Modal
      show={show}
      title={
        <div className="flex w-full flex-col gap-y-5 sm:w-[492px]">
          <Heading
            containerClassName="items-center"
            heading={t("materials.grant_access")}
            left={
              <Circle>
                <User size={24} className="text-tertiary-800" />
              </Circle>
            }
          />
          <p className="text-base font-normal text-neutral-600">
            {t("materials.grant_access_description")}
          </p>
        </div>
      }
      onClose={onClose}
    >
      <div className="flex flex-col gap-y-5">
        {/* Access type toggle */}
        <div className="flex rounded-xl border border-neutral-200 bg-neutral-50 p-1">
          <button
            onClick={() => handleAccessTypeChange("bundle")}
            className={`flex flex-1 items-center justify-center gap-x-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              accessType === "bundle"
                ? "bg-white text-neutral-900 shadow-sm"
                : "text-neutral-500 hover:text-neutral-700"
            }`}
          >
            <Package size={16} />
            {t("materials.bundle")}
          </button>
          <button
            onClick={() => handleAccessTypeChange("course")}
            className={`flex flex-1 items-center justify-center gap-x-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              accessType === "course"
                ? "bg-white text-neutral-900 shadow-sm"
                : "text-neutral-500 hover:text-neutral-700"
            }`}
          >
            <Package size={16} />
            {t("materials.course")}
          </button>
        </div>

        {/* User search */}
        <div className="flex flex-col gap-y-1">
          <label className="text-xs font-medium text-neutral-600">
            {t("materials.search_user")}
          </label>

          {preselectedUser ? (
            <div className="flex items-center gap-x-2 rounded-xl border border-tertiary-200 bg-tertiary-50 px-3 py-3">
              <Check size={14} className="text-tertiary-600" />
              <span className="text-sm text-tertiary-800">
                {preselectedUser.first_name} {preselectedUser.last_name} —{" "}
                {preselectedUser.email}
              </span>
            </div>
          ) : (
            <>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  {isSearching ? (
                    <Spinner className="h-4 w-4 text-neutral-400" />
                  ) : (
                    <MagnifyingGlass size={16} className="text-neutral-400" />
                  )}
                </div>
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    if (selectedUser) setSelectedUser(null);
                    setShowResults(true);
                  }}
                  placeholder={t("materials.search_user_placeholder")}
                  className="block w-full rounded-xl border border-neutral-400 p-3 pl-9 pr-9 text-sm text-neutral-950 placeholder:text-neutral-400 hover:ring-1 hover:ring-tertiary-600 focus:shadow-input focus:outline-none focus:ring-1 focus:ring-tertiary-600"
                />
                {userSearch && (
                  <button
                    onClick={handleClearUser}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400 hover:text-neutral-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Search results dropdown */}
              {showResults && userResults.length > 0 && !selectedUser && (
                <div className="rounded-xl border border-neutral-200 bg-white shadow-md">
                  {userResults.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleUserSelect(user)}
                      className="flex w-full flex-col px-4 py-2.5 text-left first:rounded-t-xl last:rounded-b-xl hover:bg-neutral-50"
                    >
                      <span className="text-sm font-medium text-neutral-900">
                        {user.first_name} {user.last_name}
                      </span>
                      <span className="text-xs text-neutral-500">
                        {user.email}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {selectedUser && (
                <div className="flex items-center gap-x-2 rounded-xl border border-tertiary-200 bg-tertiary-50 px-3 py-2">
                  <Check size={14} className="text-tertiary-600" />
                  <span className="text-sm text-tertiary-800">
                    {selectedUser.first_name} {selectedUser.last_name} —{" "}
                    {selectedUser.email}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Bundle or Course select */}
        {accessType === "bundle" ? (
          <div className="flex flex-col gap-y-1">
            <label className="text-xs font-medium text-neutral-600">
              {t("materials.select_bundle")}
            </label>
            <select
              value={selectedBundle?.id ?? ""}
              onChange={(e) => {
                const bundle =
                  bundles.find((b) => b.id === e.target.value) ?? null;
                setSelectedBundle(bundle);
              }}
              className="block w-full rounded-xl border border-neutral-400 p-3 text-sm text-neutral-950 hover:ring-1 hover:ring-tertiary-600 focus:shadow-input focus:outline-none focus:ring-1 focus:ring-tertiary-600"
            >
              <option value="">
                {t("materials.select_bundle_placeholder")}
              </option>
              {bundles.map((bundle) => (
                <option key={bundle.id} value={bundle.id}>
                  {`${bundle.name} — ${mapCourseTypeLabel(bundle.type)}`}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="flex flex-col gap-y-1">
            <label className="text-xs font-medium text-neutral-600">
              {t("materials.select_course")}
            </label>
            <select
              value={selectedCourse?.id ?? ""}
              onChange={(e) => {
                const course =
                  courses.find((c) => c.id === e.target.value) ?? null;
                setSelectedCourse(course);
              }}
              className="block w-full rounded-xl border border-neutral-400 p-3 text-sm text-neutral-950 hover:ring-1 hover:ring-tertiary-600 focus:shadow-input focus:outline-none focus:ring-1 focus:ring-tertiary-600"
            >
              <option value="">
                {t("materials.select_course_placeholder")}
              </option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Access period */}
        <div className="flex flex-col gap-y-3 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-4">
          <p className="text-sm font-medium text-neutral-700">
            {t("materials.access_period")}
          </p>

          <div className="flex flex-col gap-y-3">
            <div className="flex flex-col gap-y-1">
              <label className="text-xs font-medium text-neutral-600">
                {t("materials.access_start_date")}{" "}
                <span className="font-normal text-neutral-400">
                  ({t("materials.optional")})
                </span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="block w-full rounded-xl border border-neutral-400 p-3 text-sm text-neutral-950 hover:ring-1 hover:ring-tertiary-600 focus:shadow-input focus:outline-none focus:ring-1 focus:ring-tertiary-600"
              />
            </div>

            <div className="flex flex-col gap-y-1">
              <label className="text-xs font-medium text-neutral-600">
                {t("materials.access_end_date")}
              </label>
              {indefinite ? (
                <div className="flex items-center rounded-xl border border-neutral-200 bg-white p-3">
                  <span className="text-sm italic text-neutral-500">
                    {t("materials.indefinite_access")}
                  </span>
                </div>
              ) : (
                <input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="block w-full rounded-xl border border-neutral-400 p-3 text-sm text-neutral-950 hover:ring-1 hover:ring-tertiary-600 focus:shadow-input focus:outline-none focus:ring-1 focus:ring-tertiary-600"
                />
              )}
              <label className="mt-1 flex cursor-pointer items-center gap-x-2">
                <input
                  type="checkbox"
                  checked={indefinite}
                  onChange={(e) => {
                    setIndefinite(e.target.checked);
                    if (e.target.checked) setEndDate("");
                  }}
                  className="h-4 w-4 rounded border-neutral-400 accent-tertiary-600"
                />
                <span className="text-xs text-neutral-600">
                  {t("materials.no_expiry_date")}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-x-2">
          <Button
            onClick={onClose}
            className="flex-grow"
            variant="outlined"
            disabled={isPending}
          >
            {t("general.cancel")}
          </Button>
          <Button
            onClick={handleGrant}
            className="flex-grow"
            variant="filled"
            disabled={!canSubmit || isPending}
          >
            {isPending ? (
              <Spinner className="h-5 w-5 animate-spin" />
            ) : (
              t("materials.grant_access")
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
